import { useCallback, useRef, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonPage,
  IonTextarea,
} from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { Avatar } from '../components/ui/Avatar';
import { AvatarUploadModal } from '../components/profile/AvatarUploadModal';
import { EditEmailModal } from '../components/profile/EditEmailModal';
import { EditPhoneModal } from '../components/profile/EditPhoneModal';
import { ProfileLocationModal } from '../components/location/ProfileLocationModal';
import { DietaryPreferencesEditor } from '../components/profile/DietaryPreferencesEditor';
import { useProfileStore } from '../store/useProfileStore';
import {
  useActiveProfile,
  useUpdateProfileBio,
  useUpdateProfileDisplayName,
} from '../hooks/useProfile';
import { logout } from '../services/authService';
import type { Address } from '../types/address';

// This page is the slimmed-down profile: identity only (photo, name, bio,
// contact, location). Everything else that used to live here now has its own
// page reached from the profile sidebar (ProfileMenu):
//   - notifications / appearance / language  -> Settings & Notifications
//   - connections / listing reach            -> Connections & Visibility
//   - listing history ("My Dishes")          -> My Kitchen
// Chef application moved to the sidebar too.

function formatProfileAddress(addr: Address | undefined): string[] {
  if (!addr) return [];
  const lines: string[] = [];
  if (addr.street1) {
    lines.push([addr.street1, addr.street2].filter(Boolean).join(' '));
  }
  const cityState = [addr.city, addr.stateProvince].filter(Boolean).join(', ');
  const cityStateZip = [cityState, addr.postalCode].filter(Boolean).join(' ');
  const finalLine = !addr.city && addr.country
    ? [cityStateZip, addr.country].filter(Boolean).join(', ')
    : cityStateZip;
  if (finalLine) lines.push(finalLine);
  return lines;
}

// Small icon affordance for the per-field edit/add actions, mirroring the
// pencil next to the display name. `add` shows a plus (field empty), edit
// shows a pencil. Kept borderless/muted so the fields read as content first,
// action second.
const FieldActionButton: React.FC<{
  onClick: () => void;
  variant: 'edit' | 'add';
  label: string;
}> = ({ onClick, variant, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="shrink-0 text-brand-primary hover:opacity-80"
  >
    {variant === 'add' ? (
      <Plus className="w-4 h-4" />
    ) : (
      <Pencil className="w-4 h-4" />
    )}
  </button>
);

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation('profile');
  // Always refetch the active profile on mount. Without this, ProfilePage
  // renders from the store's last whoami snapshot, which can be stale
  // relative to BE-side mutations the FE didn't initiate (auto-flips from
  // /device, admin edits, another device's settings change). useActiveProfile
  // pushes the fresh value back into the store so non-query consumers
  // (gates, the avatar in TopBar, etc.) also see it.
  useActiveProfile();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const queryClient = useQueryClient();
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [draftBio, setDraftBio] = useState('');
  const updateDisplayName = useUpdateProfileDisplayName();
  const updateBio = useUpdateProfileBio();

  const handleSignOut = async () => {
    await logout();
  };

  const contentRef = useRef<HTMLIonContentElement>(null);
  /**
   * Pull-to-refresh: invalidate the active profile query so the identity
   * fields on this page refetch fresh from BE. Stable identity via useCallback
   * — PullToRefresh rebinds its gesture whenever `onRefresh` changes. (The
   * spinner is released even on failure by PullToRefresh itself, so there's no
   * completion call to make here.)
   */
  const handleRefresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
    [queryClient],
  );

  const profileAddress = activeProfile?.metadata?.addresses?.find((a) => a.type === 'profile');
  const locationLines = formatProfileAddress(profileAddress);

  const startEditingName = () => {
    setDraftName(activeProfile?.displayName ?? '');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setDraftName('');
  };

  const saveDisplayName = () => {
    if (!activeProfile) return;
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === activeProfile.displayName) {
      cancelEditingName();
      return;
    }
    updateDisplayName.mutate(
      { profileId: activeProfile.id, displayName: trimmed },
      { onSuccess: () => cancelEditingName() },
    );
  };

  const startEditingBio = () => {
    setDraftBio(activeProfile?.bio ?? '');
    setIsEditingBio(true);
  };

  const cancelEditingBio = () => {
    setIsEditingBio(false);
    setDraftBio('');
  };

  const saveBio = () => {
    if (!activeProfile) return;
    const trimmed = draftBio.trim();
    if (trimmed === (activeProfile.bio ?? '')) {
      cancelEditingBio();
      return;
    }
    updateBio.mutate(
      { profileId: activeProfile.id, bio: trimmed },
      { onSuccess: () => cancelEditingBio() },
    );
  };

  // Title band hides on scroll-down, returns on scroll-up.
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('menu.profile')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-6">
          {/* Profile Photo Section */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setShowAvatarModal(true)}
              className="relative group"
            >
              <Avatar
                src={activeProfile?.avatarUrl}
                name={activeProfile?.displayName}
                size="lg"
                className="!w-24 !h-24"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/20 transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-8 h-8 text-white opacity-0 group-hover:opacity-70 transition-opacity"
                >
                  <path d="M12 9a3.75 3.75 0 100 7.5A3.75 3.75 0 0012 9z" />
                  <path
                    fillRule="evenodd"
                    d="M9.344 3.071a49.52 49.52 0 015.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 01-3 3H4.5a3 3 0 01-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.152-.177a1.56 1.56 0 001.11-.71l.822-1.315a2.942 2.942 0 012.332-1.39zM6.75 12.75a5.25 5.25 0 1110.5 0 5.25 5.25 0 01-10.5 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
            <div className="text-center w-full">
              {isEditingName ? (
                <div className="flex flex-col items-center gap-2">
                  <IonItem className="w-full">
                    <IonInput
                      label={t('displayNameLabel')}
                      labelPlacement="stacked"
                      value={draftName}
                      onIonInput={(e) => setDraftName(e.detail.value ?? '')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveDisplayName();
                        }
                      }}
                      autofocus
                      disabled={updateDisplayName.isPending}
                    />
                  </IonItem>
                  <div className="flex gap-2">
                    <IonButton
                      size="small"
                      fill="outline"
                      onClick={cancelEditingName}
                      disabled={updateDisplayName.isPending}
                    >
                      {t('common:actions.cancel')}
                    </IonButton>
                    <IonButton
                      size="small"
                      onClick={saveDisplayName}
                      disabled={updateDisplayName.isPending || !draftName.trim()}
                    >
                      {t('common:actions.save')}
                    </IonButton>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-lg font-medium">
                      {activeProfile?.displayName || activeProfile?.email}
                    </p>
                    <button
                      type="button"
                      onClick={startEditingName}
                      className="text-brand-primary hover:opacity-80"
                      aria-label={t('displayNameAria')}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <IonButton expand="block" color="danger" onClick={handleSignOut}>
              {t('actions.signOut')}
            </IonButton>
          </div>

          {/* Bio Section */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium">{t('bio.title')}</h3>
              {!isEditingBio && (
                <FieldActionButton
                  onClick={startEditingBio}
                  variant="edit"
                  label={t('common:actions.edit')}
                />
              )}
            </div>
            {isEditingBio ? (
              <div className="space-y-2">
                <IonItem>
                  <IonTextarea
                    label={t('bio.title')}
                    labelPlacement="stacked"
                    value={draftBio}
                    onIonInput={(e) => setDraftBio(e.detail.value ?? '')}
                    maxlength={500}
                    counter
                    autoGrow
                    rows={3}
                    placeholder={t('bio.placeholder')}
                    disabled={updateBio.isPending}
                  />
                </IonItem>
                <div className="flex justify-end gap-2">
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={cancelEditingBio}
                    disabled={updateBio.isPending}
                  >
                    {t('common:actions.cancel')}
                  </IonButton>
                  <IonButton size="small" onClick={saveBio} disabled={updateBio.isPending}>
                    {t('common:actions.save')}
                  </IonButton>
                </div>
              </div>
            ) : activeProfile?.bio ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{activeProfile.bio}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('bio.notSet')}</p>
            )}
          </div>

          {/* Contact Section */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <h3 className="font-medium">{t('contact.title')}</h3>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t('contact.emailLabel')}</p>
                {activeProfile?.email ? (
                  <p className="text-sm truncate">{activeProfile.email}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('contact.notSet')}</p>
                )}
              </div>
              <FieldActionButton
                onClick={() => setShowEmailModal(true)}
                variant={activeProfile?.email ? 'edit' : 'add'}
                label={activeProfile?.email ? t('common:actions.edit') : t('contact.addCta')}
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t('contact.phoneLabel')}</p>
                {activeProfile?.phoneE164 ? (
                  <p className="text-sm truncate">{activeProfile.phoneE164}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('contact.notSet')}</p>
                )}
              </div>
              <FieldActionButton
                onClick={() => setShowPhoneModal(true)}
                variant={activeProfile?.phoneE164 ? 'edit' : 'add'}
                label={activeProfile?.phoneE164 ? t('common:actions.edit') : t('contact.addCta')}
              />
            </div>
          </div>

          {/* Location Section */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium">{t('location.title')}</h3>
              <FieldActionButton
                onClick={() => setShowLocationModal(true)}
                variant={locationLines.length > 0 ? 'edit' : 'add'}
                label={locationLines.length > 0 ? t('common:actions.edit') : t('contact.addCta')}
              />
            </div>
            {locationLines.length > 0 ? (
              <div className="text-sm text-muted-foreground">
                {locationLines.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('location.notSet')}</p>
            )}
          </div>

          {/* Dietary Section. Same editor the home-feed prompt card opens in a
              popup — one component, so the two surfaces can't drift. */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="space-y-1">
              <h3 className="font-medium">{t('dietary.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('dietary.description')}</p>
            </div>
            <DietaryPreferencesEditor />
          </div>
        </div>
        </PullToRefresh>
      </IonContent>

      <AvatarUploadModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
      />
      <ProfileLocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
      />
      <EditEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
      />
      <EditPhoneModal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
      />
    </IonPage>
  );
};
